/**
 * GLContext — обёртка WebGL2-контекста.
 *
 * Управляет:
 * - Глобальным UBO (binding point 0) для `u_projection`, `u_dpr`, `u_time`
 * - Дефолтными настройками конвейера (blend, depth)
 *
 * Не зависит от `Game` — контекст передаётся через конструктор.
 */
export class GLContext
{
  /** Binding point для глобального UBO (используется всеми шейдерами) */
  public static readonly GLOBAL_UBO_BINDING_POINT: number = 0;
  /** Имя uniform block в GLSL, к которому привязывается UBO */
  public static readonly GLOBAL_UBO_BLOCK_NAME: string = "GlobalParams";
  /** Кількість буферів для UBO (Double/Triple buffering) */
  public static readonly UBO_RING_BUFFER_COUNT: number = 3;

  public readonly gl: WebGL2RenderingContext;
  private readonly global_ubos: WebGLBuffer[] = [];
  private current_ubo_index: number = 0;

  // std140: mat4 (16 floats, 64 bytes)
  // float u_dpr (1 float, 4 bytes)
  // float u_time (1 float, 4 bytes)
  // padded to 20 floats
  private readonly ubo_data: Float32Array = new Float32Array( 20 );

  constructor( gl: WebGL2RenderingContext )
  {
    this.gl = gl;

    // Создаём глобальные UBO (Ring Buffering) для обходу фрізів у Safari
    for ( let i: number = 0; i < GLContext.UBO_RING_BUFFER_COUNT; ++i )
    {
      const ubo: WebGLBuffer = gl.createBuffer()!;
      gl.bindBuffer( gl.UNIFORM_BUFFER, ubo );
      gl.bufferData( gl.UNIFORM_BUFFER, this.ubo_data, gl.DYNAMIC_DRAW );
      this.global_ubos.push( ubo );
    }
    
    // Прив'язуємо початковий буфер
    gl.bindBufferBase( gl.UNIFORM_BUFFER, GLContext.GLOBAL_UBO_BINDING_POINT, this.global_ubos[0] );
    gl.bindBuffer( gl.UNIFORM_BUFFER, null );

    this.restoreDefaults();
  }

  public updateGlobalParams( dpr: number, now: number ): void
  {
    const gl: WebGL2RenderingContext = this.gl;
    const canvas: HTMLCanvasElement = gl.canvas as HTMLCanvasElement;
    
    // Ortho projection directly to clip space [-1, 1] for logical pixel positions
    const logical_w: number = canvas.width / dpr;
    const logical_h: number = canvas.height / dpr;
    
    const lr: number = 1.0 / logical_w;
    const bt: number = 1.0 / -logical_h;

    // mat4 column-major:
    this.ubo_data[0]  = 2.0 * lr;  this.ubo_data[1]  = 0.0;        this.ubo_data[2]  = 0.0;  this.ubo_data[3]  = 0.0;
    this.ubo_data[4]  = 0.0;       this.ubo_data[5]  = 2.0 * bt;   this.ubo_data[6]  = 0.0;  this.ubo_data[7]  = 0.0;
    this.ubo_data[8]  = 0.0;       this.ubo_data[9]  = 0.0;        this.ubo_data[10] = -1.0; this.ubo_data[11] = 0.0;
    this.ubo_data[12] = -1.0;      this.ubo_data[13] = 1.0;        this.ubo_data[14] = 0.0;  this.ubo_data[15] = 1.0;
    
    // u_dpr starts at offset 16
    this.ubo_data[16] = dpr;
    // u_time starts at offset 17
    this.ubo_data[17] = now;

    // Triple Buffering: беремо наступний буфер по колу,
    // щоб уникнути блокування драйвера (150ms stalls в Safari), 
    // якщо попередній буфер ще читається GPU.
    this.current_ubo_index = ( this.current_ubo_index + 1 ) % GLContext.UBO_RING_BUFFER_COUNT;
    const current_ubo: WebGLBuffer = this.global_ubos[this.current_ubo_index];

    gl.bindBuffer( gl.UNIFORM_BUFFER, current_ubo );
    gl.bufferSubData( gl.UNIFORM_BUFFER, 0, this.ubo_data );
    gl.bindBufferBase( gl.UNIFORM_BUFFER, GLContext.GLOBAL_UBO_BINDING_POINT, current_ubo );
    gl.bindBuffer( gl.UNIFORM_BUFFER, null );
  }

  /** Восстанавливает дефолтные настройки WebGL-конвейера для 2D */
  public restoreDefaults(): void
  {
    const gl: WebGL2RenderingContext = this.gl;
    gl.disable( gl.DEPTH_TEST );
    gl.depthMask( false );
    gl.enable( gl.BLEND );
    gl.blendEquation( gl.FUNC_ADD );
    gl.blendFunc( gl.ONE, gl.ONE_MINUS_SRC_ALPHA ); // Premultiplied Alpha
  }

  /** Очищает кадр */
  public clear(): void
  {
    this.gl.clear( this.gl.COLOR_BUFFER_BIT );
  }

  public dispose(): void
  {
    for ( const ubo of this.global_ubos )
      this.gl.deleteBuffer( ubo );
  }
}

